import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost, getApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { Card } from '../../components/Card';
import { PageScaffold } from '../../components/PageScaffold';
import { Input } from '../../components/Input';
import { Button } from '../../components/Button';
import { Money } from '../../components/Money';
import { StatusPill } from '../../components/StatusPill';
import { EmptyState } from '../../components/EmptyState';
import { Modal } from '../../components/Modal';
import { cn } from '../../lib/cn';
import { CashFlowPage } from '../finance/CashFlowPage';

interface Shop {
  id: string;
  code: string;
  name: string;
}

interface CashCloseToday {
  shopId: string;
  expectedCash: string;
  /** The system's figure at the moment of counting — see the controller note. */
  expectedAtClose: string | null;
  /** Cheques the shop is holding, un-banked, per its own cheque account. */
  expectedCheques: string;
  expectedChequesAtClose: string | null;
  actualCheques: string | null;
  chequeDiscrepancy: string | null;
  /** The individual instruments behind that total, so they can be ticked off. */
  chequesHeld: Array<{
    id: string;
    amount: string;
    reference: string | null;
    at: string;
    orderId: string | null;
    orderNumber: string | null;
    customerName: string | null;
  }>;
  cashToday?: string;
  actualCash: string | null;
  discrepancy: string | null;
  closedAt: string | null;
  reason: string | null;
  /**
   * How the system reached `expectedCash`: the last COUNTED float, plus cash
   * taken since, minus what was handed to a driver / banked / paid out. Null in
   * sandbox, which has no GL to derive it from.
   */
  derivation: {
    openingFloat: string;
    openingLabel: string;
    cashIn: string;
    cashOut: string;
    bySource: Array<{ source: string; label: string; count: number; in: string; out: string }>;
  } | null;
}

interface DrawerMovement {
  id: string;
  at: string;
  source: string;
  label: string;
  orderId: string | null;
  orderNumber: string | null;
  amount: string; // signed: + in, − out
}

interface DrawerBreakdown {
  shopId: string;
  balance: string;
  opening: string;
  openingLabel: string;
  movements: DrawerMovement[];
  truncated: boolean;
}

interface CashCloseHistory {
  id: string;
  day: string;
  expectedCash: string;
  actualCash: string;
  discrepancy: string;
  reason: string | null;
  closedAt: string;
  shop: { code: string; name: string };
  closedBy: { name: string };
}

/**
 * One navigation entry, two jobs:
 * - shop staff count and close the till;
 * - HQ users see the current cash position and audit trail across shops.
 *
 * Keeping these as separate child components prevents the HQ page from loading
 * every shop's close form/history and keeps the shop-floor workflow unchanged.
 */
export function CashClosePage(): JSX.Element {
  const { perms } = useAuth();
  return perms.has('orders.view_all_shops') ? <CashFlowPage /> : <ShopCashClosePage />;
}

function ShopCashClosePage(): JSX.Element {
  const { user, perms } = useAuth();
  // Cross-shop overseer (owner/admin/operations) sees an all-shops roll-up and a
  // shop picker; a shop-floor closer (sales girl) sees only her own till. Keyed
  // off the permission, not the absence of a home shop (ops now home at HQ).
  const isHqUser = perms.has('orders.view_all_shops');
  const qc = useQueryClient();

  // HQ roles (owner / admin / finance) pick which shop to close for.
  // Shop staff are locked to their home shop.
  const shopsQuery = useQuery({
    queryKey: ['shops'],
    queryFn: () => apiGet<Shop[]>('/shops'),
    enabled: isHqUser,
  });
  const shops = shopsQuery.data ?? [];
  const [searchParams, setSearchParams] = useSearchParams();
  const [pickedShopId, setPickedShopId] = useState<string>(
    searchParams.get('shopId') ?? user?.activeShopId ?? user?.homeShopId ?? '',
  );
  useEffect(() => {
    if (!pickedShopId && shops[0]) setPickedShopId(shops[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shops.length]);
  // Keep ?shopId in sync so the view is bookmarkable and back-button friendly.
  useEffect(() => {
    if (!isHqUser || !pickedShopId) return;
    if (searchParams.get('shopId') === pickedShopId) return;
    const next = new URLSearchParams(searchParams);
    next.set('shopId', pickedShopId);
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickedShopId, isHqUser]);

  // HQ picks a shop; a shop-floor closer follows wherever she's currently
  // switched to (activeShopId) so switching shops in the nav repoints the till
  // live, rather than sticking to the shop she first landed on.
  const shopId = isHqUser
    ? pickedShopId || null
    : user?.activeShopId ?? user?.homeShopId ?? null;

  const today = useQuery({
    queryKey: ['cash-close-today', shopId],
    queryFn: () => apiGet<CashCloseToday>('/cash-close/today', shopId ? { shopId } : {}),
    enabled: !!shopId,
  });
  const history = useQuery({
    queryKey: ['cash-close-history', shopId],
    queryFn: () => apiGet<CashCloseHistory[]>('/cash-close', shopId ? { shopId } : {}),
    enabled: !!shopId,
  });

  const [actual, setActual] = useState('');
  const [actualCheques, setActualCheques] = useState('');
  const [reason, setReason] = useState('');
  const [breakdownOpen, setBreakdownOpen] = useState(false);

  const breakdown = useQuery({
    queryKey: ['cash-close-breakdown', shopId],
    queryFn: () => apiGet<DrawerBreakdown>('/cash-close/breakdown', shopId ? { shopId } : {}),
    enabled: !!shopId && breakdownOpen,
  });

  const submit = useMutation({
    mutationFn: () =>
      apiPost('/cash-close', {
        day: new Date().toISOString().slice(0, 10),
        actualCash: Number(actual),
        // Sent only when she actually counted them — omitted means "cheques not
        // counted", which the server records distinctly from a zero count.
        ...(actualCheques !== '' ? { actualCheques: Number(actualCheques) } : {}),
        reason: reason || undefined,
        ...(isHqUser && shopId ? { shopId } : {}),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cash-close-today'] });
      qc.invalidateQueries({ queryKey: ['cash-close-history'] });
      setActual('');
      setReason('');
    },
  });

  // HQ overview: per-shop "today" in parallel so the owner sees every shop's cash state at a glance.
  // MUST be declared before any early returns to satisfy Rules of Hooks.
  const perShopToday = useQueries({
    queries: isHqUser
      ? shops.map((s) => ({
          queryKey: ['cash-close-today', s.id],
          queryFn: () => apiGet<CashCloseToday>('/cash-close/today', { shopId: s.id }),
          staleTime: 60_000,
        }))
      : [],
  });

  // HQ user with no shops loaded yet
  if (isHqUser && shopsQuery.isLoading) {
    return (
      <PageScaffold title="Cash close">
        <Card>
          <div className="text-center text-ink-3 py-8">Loading shops…</div>
        </Card>
      </PageScaffold>
    );
  }
  if (isHqUser && shops.length === 0) {
    return (
      <PageScaffold title="Cash close">
        <Card>
          <EmptyState
            title="No shops yet"
            description="Add a shop under Admin → Shops to enable cash close."
          />
        </Card>
      </PageScaffold>
    );
  }
  if (!shopId) {
    return (
      <PageScaffold title="Cash close">
        <Card>
          <EmptyState
            title="No shop assigned"
            description="Cash close is per-shop. Your account isn't assigned to a shop. Ask an admin to set your home shop."
          />
        </Card>
      </PageScaffold>
    );
  }

  const expectedNum = Number(today.data?.expectedCash ?? 0);
  const actualNum = Number(actual || today.data?.actualCash || 0);
  const discrepancyNow = actualNum - expectedNum;
  // Mirrors the server's `reason_required` gate so the sales girl sees the ask
  // before she submits, not as a rejection afterwards. The server stays the
  // real enforcement point — this is just the earlier, kinder half of it.
  const needsReason = !!actual && discrepancyNow !== 0 && reason.trim() === '';

  // Cheques reconcile independently of cash — see the controller. A shop with
  // no cheques on hand never sees the field.
  const expectedChequesNum = Number(today.data?.expectedCheques ?? 0);
  const chequesCounted = actualCheques !== '';
  const chequeDiffNow = chequesCounted ? Number(actualCheques) - expectedChequesNum : 0;
  const holdsCheques = expectedChequesNum !== 0 || (today.data?.chequesHeld ?? []).length > 0;
  const needsChequeReason = chequesCounted && chequeDiffNow !== 0 && reason.trim() === '';
  const blocked = needsReason || needsChequeReason;

  // HQ aggregate — sum today's expected/counted/discrepancy across every shop.
  // Counted only includes shops that have actually closed today; unclosed
  // shops contribute to "open" count, not to the counted total.
  const hqTotals = isHqUser
    ? perShopToday.reduce(
        (acc, r) => {
          const data = r.data;
          if (!data) return acc;
          acc.expected += Number(data.expectedCash ?? 0);
          if (data.actualCash != null) {
            acc.counted += Number(data.actualCash);
            acc.discrepancy += Number(data.discrepancy ?? 0);
            acc.closedShops += 1;
          } else {
            acc.openShops += 1;
          }
          return acc;
        },
        { expected: 0, counted: 0, discrepancy: 0, closedShops: 0, openShops: 0 },
      )
    : null;

  return (
    <PageScaffold
      title="Cash close"
      subtitle={`End-of-day reconciliation. Today is ${new Date().toLocaleDateString('en-MU', { weekday: 'long', day: 'numeric', month: 'long' })}.`}
      actions={
        isHqUser && shops.length > 0 ? (
          <label className="flex flex-col gap-1">
            <span className="text-[10px] uppercase tracking-[0.18em] text-ink-3">Viewing shop</span>
            <select
              value={pickedShopId}
              onChange={(e) => setPickedShopId(e.target.value)}
              className="bg-surface border border-rule rounded px-3 py-2 text-sm text-ink min-w-[200px] focus:outline-none focus:border-accent"
            >
              {shops.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.code} — {s.name}
                </option>
              ))}
            </select>
          </label>
        ) : undefined
      }
    >
      {/* HQ-only — across-the-floor today summary */}
      {isHqUser && shops.length > 0 && (
        <Card title="Today across all shops" subtitle="Tap a shop to drill in" bodyClassName="p-4">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {shops.map((s, idx) => {
              const r = perShopToday[idx];
              const data = r?.data;
              const closed = !!data?.closedAt;
              const disc = Number(data?.discrepancy ?? 0);
              const chequeDisc = Number(data?.chequeDiscrepancy ?? 0);
              const d = data?.derivation ?? null;
              const isPicked = s.id === pickedShopId;
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => setPickedShopId(s.id)}
                  className={cn(
                    'text-left rounded border px-3 py-3 transition focus:outline-none focus:ring-2 focus:ring-accent/40',
                    isPicked
                      ? 'border-accent bg-accent-soft'
                      : 'border-rule hover:border-rule-2 hover:bg-paper-2',
                  )}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium tracking-wide text-ink">{s.code}</span>
                    <span
                      className={cn(
                        'text-[9px] px-1.5 py-0.5 rounded uppercase tracking-wider',
                        closed ? 'bg-positive-soft text-positive' : 'bg-paper-2 text-ink-3',
                      )}
                    >
                      {closed ? 'closed' : 'open'}
                    </span>
                  </div>
                  <div className="text-[11px] text-ink-3 mt-0.5 truncate">{s.name}</div>
                  <div className="mt-2 space-y-0.5">
                    {/* The DERIVATION, not just the answer. One "Expected"
                        figure can't tell a quiet shop from a busy one that
                        handed most of its takings to a driver — and the owner
                        can't judge the girl's count without seeing what the
                        system thinks went in and out since it was last verified. */}
                    {d && (
                      <>
                        <div className="flex justify-between items-baseline gap-1 text-[11px]">
                          {/* Smaller and nowrap: the date carries real meaning
                              ("counted WHEN"), so it must not clip to "202…"
                              in a five-across grid. */}
                          <span className="text-ink-3 text-[10px] whitespace-nowrap">
                            {d.openingLabel}
                          </span>
                          <Money value={d.openingFloat} className="text-ink-3" />
                        </div>
                        <div className="flex justify-between text-[11px]">
                          <span className="text-ink-3">Sales in</span>
                          <span className="text-positive">
                            +<Money value={d.cashIn} className="text-current" />
                          </span>
                        </div>
                        <div className="flex justify-between text-[11px]">
                          <span className="text-ink-3">Handed over / out</span>
                          <span className="text-negative">
                            −<Money value={d.cashOut} className="text-current" />
                          </span>
                        </div>
                      </>
                    )}
                    <div
                      className={cn(
                        'flex justify-between text-[11px]',
                        d && 'pt-1 border-t border-rule-2 mt-1',
                      )}
                    >
                      <span className="text-ink-3">System says</span>
                      {/* Once closed, show what the system said when she
                          counted — the live balance has been snapped to her
                          figure by the over/short entry. */}
                      <Money
                        value={data?.expectedAtClose ?? data?.expectedCash ?? 0}
                        className="text-ink font-medium"
                      />
                    </div>
                    <div className="flex justify-between text-[11px]">
                      <span className="text-ink-3">She counted</span>
                      {data?.actualCash != null ? (
                        <Money value={data.actualCash} className="text-ink" />
                      ) : (
                        <span className="text-ink-3">not yet</span>
                      )}
                    </div>
                    {/* The flag. A mismatch between what the books say and what
                        was physically counted is the one thing on this card
                        that needs somebody to do something. */}
                    {closed && disc !== 0 && (
                      <div
                        className={cn(
                          'mt-1.5 -mx-1 px-1.5 py-1 rounded text-[11px] font-medium flex justify-between items-center',
                          disc > 0 ? 'bg-warning-soft text-warning' : 'bg-negative-soft text-negative',
                        )}
                      >
                        <span>⚠ {disc > 0 ? 'Over' : 'Short'}</span>
                        <Money value={Math.abs(disc)} className="text-current" />
                      </div>
                    )}
                    {/* Cheques get their own flag: a cash overage must never
                        visually cancel a missing cheque. */}
                    {closed && chequeDisc !== 0 && (
                      <div
                        className={cn(
                          'mt-1 -mx-1 px-1.5 py-1 rounded text-[11px] font-medium flex justify-between items-center',
                          chequeDisc > 0
                            ? 'bg-warning-soft text-warning'
                            : 'bg-negative-soft text-negative',
                        )}
                      >
                        <span>⚠ Cheques {chequeDisc > 0 ? 'over' : 'short'}</span>
                        <Money value={Math.abs(chequeDisc)} className="text-current" />
                      </div>
                    )}
                    {closed && (disc !== 0 || chequeDisc !== 0) && !data?.reason && (
                      <div className="text-[10px] text-negative mt-0.5">No reason given</div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </Card>
      )}

      <div className={cn('grid grid-cols-1 gap-6', isHqUser ? '' : 'md:grid-cols-3')}>
        <Card
          title={isHqUser ? "Today's totals · all shops" : "Today's totals"}
          subtitle={
            isHqUser && hqTotals
              ? `${hqTotals.closedShops} closed · ${hqTotals.openShops} still open`
              : undefined
          }
          className={isHqUser ? '' : 'md:col-span-1'}
        >
          {isHqUser && hqTotals ? (
            <dl className="space-y-3">
              <Row
                label="Expected cash (all shops)"
                value={<Money value={hqTotals.expected.toFixed(2)} className="text-ink" />}
              />
              <Row
                label="Counted so far"
                value={
                  hqTotals.closedShops > 0 ? (
                    <Money value={hqTotals.counted.toFixed(2)} />
                  ) : (
                    <span className="text-ink-3 text-sm">no shops closed yet</span>
                  )
                }
              />
              {hqTotals.closedShops > 0 && hqTotals.discrepancy !== 0 && (
                <Row
                  label="Discrepancy (sum)"
                  value={
                    <span className={hqTotals.discrepancy > 0 ? 'text-warning' : 'text-negative'}>
                      {hqTotals.discrepancy > 0 ? '+' : ''}
                      <Money value={hqTotals.discrepancy.toFixed(2)} className="text-current" />
                    </span>
                  }
                />
              )}
            </dl>
          ) : (
            <dl className="space-y-3">
              <Row
                label="Cash in drawer (expected)"
                value={<Money value={today.data?.expectedCash} className="text-ink" />}
              />
              {holdsCheques && (
                <Row
                  label="Cheques held (expected)"
                  value={<Money value={today.data?.expectedCheques} className="text-ink" />}
                />
              )}
              {today.data?.cashToday != null && (
                <Row
                  label="Cash taken today"
                  value={<Money value={today.data.cashToday} className="text-ink-3" />}
                />
              )}
              <Row
                label="Already recorded"
                value={
                  today.data?.actualCash != null ? (
                    <Money value={today.data.actualCash} />
                  ) : (
                    <span className="text-ink-3 text-sm">not closed yet</span>
                  )
                }
              />
              {today.data?.discrepancy != null && Number(today.data.discrepancy) !== 0 && (
                <Row
                  label="Discrepancy"
                  value={
                    <span
                      className={
                        Number(today.data.discrepancy) > 0 ? 'text-warning' : 'text-negative'
                      }
                    >
                      {Number(today.data.discrepancy) > 0 ? '+' : ''}
                      <Money value={today.data.discrepancy} className="text-current" />
                    </span>
                  }
                />
              )}
              {today.data?.closedAt && (
                <Row
                  label="Closed at"
                  value={
                    <span className="text-xs text-ink-3">
                      {new Date(today.data.closedAt).toLocaleString()}
                    </span>
                  }
                />
              )}
            </dl>
          )}
          <button
            type="button"
            onClick={() => setBreakdownOpen(true)}
            className="mt-4 text-xs text-accent hover:underline focus:outline-none"
          >
            What's in the drawer{isHqUser ? ` at ${shops.find((s) => s.id === shopId)?.code ?? 'this shop'}` : ''}? →
          </button>
        </Card>

        {!isHqUser && (
          <Card
            title={today.data?.closedAt ? 'Update close' : 'Close till'}
            className="md:col-span-2"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Input
                  label="Actual cash counted (MUR)"
                  type="number"
                  min="0"
                  step="0.01"
                  value={actual}
                  onChange={(e) => setActual(e.target.value)}
                  placeholder={expectedNum.toFixed(2)}
                  autoFocus
                />
                {actual && (
                  <div className="mt-3 text-sm">
                    <span className="text-ink-3">Discrepancy: </span>
                    <span
                      className={cn(
                        'font-medium',
                        discrepancyNow === 0
                          ? 'text-positive'
                          : discrepancyNow > 0
                            ? 'text-warning'
                            : 'text-negative',
                      )}
                    >
                      {discrepancyNow > 0 ? '+' : ''}
                      <Money value={discrepancyNow} className="text-current" />
                    </span>
                  </div>
                )}
              </div>
              {/* Cheques reconcile alongside the notes — both leave on the same
                  driver round, so counting only cash verifies half the money in
                  the room. Hidden when the shop holds none. */}
              {holdsCheques && (
                <div>
                  <Input
                    label="Cheques counted (MUR)"
                    type="number"
                    min="0"
                    step="0.01"
                    value={actualCheques}
                    onChange={(e) => setActualCheques(e.target.value)}
                    placeholder={expectedChequesNum.toFixed(2)}
                  />
                  {chequesCounted ? (
                    <div className="mt-3 text-sm">
                      <span className="text-ink-3">Cheque difference: </span>
                      <span
                        className={cn(
                          'font-medium',
                          chequeDiffNow === 0
                            ? 'text-positive'
                            : chequeDiffNow > 0
                              ? 'text-warning'
                              : 'text-negative',
                        )}
                      >
                        {chequeDiffNow > 0 ? '+' : ''}
                        <Money value={chequeDiffNow} className="text-current" />
                      </span>
                    </div>
                  ) : (
                    <p className="mt-2 text-xs text-ink-3">
                      Leave blank if you're not counting cheques tonight.
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* The instruments behind the total. A figure alone is uncountable —
                to verify it she has to hold each cheque, so each one is listed
                with the number written on it and who it came from. */}
            {holdsCheques && (today.data?.chequesHeld ?? []).length > 0 && (
              <div className="mt-4 border border-rule rounded overflow-hidden">
                <div className="px-3 py-2 bg-paper-2 border-b border-rule flex justify-between items-baseline">
                  <span className="text-[10px] uppercase tracking-[0.18em] text-ink-3">
                    Cheques the system says you hold
                  </span>
                  <Money value={today.data?.expectedCheques ?? 0} className="text-ink text-sm" />
                </div>
                <ul className="divide-y divide-rule-2 max-h-52 overflow-y-auto">
                  {(today.data?.chequesHeld ?? []).map((c) => (
                    <li key={c.id} className="px-3 py-2 flex items-baseline justify-between gap-3 text-sm">
                      <span className="min-w-0">
                        <span className="kc-mono text-xs text-ink">{c.reference ?? 'no number'}</span>
                        <span className="text-ink-3 text-xs">
                          {c.customerName ? ` · ${c.customerName}` : ''}
                          {c.orderNumber ? ` · ${c.orderNumber}` : ''}
                        </span>
                      </span>
                      <Money value={c.amount} className="text-ink shrink-0" />
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {(needsReason || needsChequeReason) && (
              <div className="mt-4">
                <Input
                  label="What happened? (required)"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder={
                    needsChequeReason
                      ? 'Which cheque is missing · cheque handed to driver already · …'
                      : 'Gave change from own pocket · receipt typed twice · …'
                  }
                  autoFocus
                />
                <p className="mt-2 text-xs text-ink-3">
                  {needsChequeReason
                    ? "A cheque is a specific piece of paper with someone's name on it. Say which one is missing or extra before closing."
                    : "This gap is written off to Cash Over/Short and can't be undone. Say what happened now, while you remember it."}
                </p>
              </div>
            )}
            {submit.isError && (
              <div className="text-xs text-negative mt-3">{getApiError(submit.error).message}</div>
            )}
            <Button
              className="mt-4"
              loading={submit.isPending}
              onClick={() => submit.mutate()}
              disabled={!actual || blocked}
            >
              {today.data?.closedAt ? 'Update close' : 'Close till'}
            </Button>
          </Card>
        )}
      </div>

      <Card
        title="History"
        subtitle={`Last ${(history.data ?? []).length} closure${(history.data ?? []).length === 1 ? '' : 's'}`}
        bodyClassName="p-0"
      >
        {history.isLoading ? (
          <div className="text-center text-ink-3 px-6 py-8">Loading…</div>
        ) : (history.data ?? []).length === 0 ? (
          <EmptyState
            title="No closes yet"
            description="Once you close today's till it'll appear here."
          />
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-ink-3 bg-paper-2 border-y border-rule">
                <th className="px-6 py-2 font-medium">Day</th>
                <th className="px-6 py-2 font-medium">Shop</th>
                <th className="px-6 py-2 font-medium text-right">Expected</th>
                <th className="px-6 py-2 font-medium text-right">Actual</th>
                <th className="px-6 py-2 font-medium text-right">Discrepancy</th>
                <th className="px-6 py-2 font-medium">Reason</th>
                <th className="px-6 py-2 font-medium">By</th>
              </tr>
            </thead>
            <tbody>
              {(history.data ?? []).map((h) => {
                const disc = Number(h.discrepancy);
                return (
                  <tr key={h.id} className="border-b border-rule-2">
                    <td className="px-6 py-3">
                      {new Date(h.day).toLocaleDateString('en-MU', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                      })}
                    </td>
                    <td className="px-6 py-3">
                      <StatusPill>{h.shop.code}</StatusPill>
                    </td>
                    <td className="px-6 py-3 text-right">
                      <Money value={h.expectedCash} />
                    </td>
                    <td className="px-6 py-3 text-right">
                      <Money value={h.actualCash} />
                    </td>
                    <td className="px-6 py-3 text-right">
                      {disc === 0 ? (
                        <span className="text-positive">✓ on the dot</span>
                      ) : (
                        <span className={disc > 0 ? 'text-warning' : 'text-negative'}>
                          {disc > 0 ? '+' : ''}
                          <Money value={h.discrepancy} className="text-current" />
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-3 text-xs text-ink-3">{h.reason ?? '—'}</td>
                    <td className="px-6 py-3 text-xs text-ink-3">{h.closedBy.name}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>

      {breakdownOpen && (
        <Modal
          title="What's in the drawer"
          subtitle="The float carried from the last count, plus every cash movement since."
          size="xl"
          onClose={() => setBreakdownOpen(false)}
        >
          {breakdown.isLoading ? (
            <div className="text-center text-ink-3 py-8">Loading…</div>
          ) : breakdown.isError ? (
            <div className="text-center text-negative py-8 text-sm">
              {getApiError(breakdown.error).message}
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-baseline justify-between border-b border-rule pb-3">
                <span className="text-sm text-ink-3">Cash in drawer now</span>
                <span className="text-xl font-semibold text-ink">
                  <Money value={breakdown.data?.balance ?? 0} className="text-current" />
                </span>
              </div>

              <div className="flex items-center justify-between text-sm">
                <span className="text-ink-3">{breakdown.data?.openingLabel ?? 'Opening'}</span>
                <Money value={breakdown.data?.opening ?? 0} className="text-ink" />
              </div>

              {(breakdown.data?.movements ?? []).length === 0 ? (
                <p className="text-sm text-ink-3 py-2">
                  No cash movements since the last count — the drawer is just the carried float.
                </p>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[11px] uppercase tracking-wide text-ink-3 border-b border-rule">
                      <th className="py-2 font-medium">When</th>
                      <th className="py-2 font-medium">Source</th>
                      <th className="py-2 font-medium text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(breakdown.data?.movements ?? []).map((m) => {
                      const amt = Number(m.amount);
                      return (
                        <tr key={m.id} className="border-b border-rule-2">
                          <td className="py-2 text-xs text-ink-3 whitespace-nowrap">
                            {new Date(m.at).toLocaleDateString('en-MU', {
                              day: 'numeric',
                              month: 'short',
                            })}
                          </td>
                          <td className="py-2">
                            {m.orderNumber ? (
                              <Link
                                to={`/orders/${m.orderId}`}
                                className="text-accent hover:underline"
                                onClick={() => setBreakdownOpen(false)}
                              >
                                {m.label}
                              </Link>
                            ) : (
                              <span className="text-ink">{m.label}</span>
                            )}
                          </td>
                          <td
                            className={cn(
                              'py-2 text-right whitespace-nowrap',
                              amt < 0 ? 'text-negative' : 'text-ink',
                            )}
                          >
                            {amt < 0 ? '−' : '+'}
                            <Money value={Math.abs(amt)} className="text-current" />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}

              {breakdown.data?.truncated && (
                <p className="text-[11px] text-ink-3">
                  Showing the most recent 300 movements only.
                </p>
              )}
            </div>
          )}
        </Modal>
      )}
    </PageScaffold>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }): JSX.Element {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-ink-3">{label}</span>
      <span className="text-ink">{value}</span>
    </div>
  );
}
